import React, { useMemo } from "react";
import { getAllStaff, getStaffTargets } from "../../utils/staff";

export default function Leaderboard({ logs, weekStartsOn = 1 }) {
  const staff = getAllStaff();
  const targets = getStaffTargets();

  const { currentWeek, lastWeek } = useMemo(() => {
    const now = new Date();
    const ws = startOfWeek(now, weekStartsOn);
    const we = endOfWeek(now, weekStartsOn);
    const lws = new Date(ws.getTime() - 7 * 86400000);
    const lwe = new Date(we.getTime() - 7 * 86400000);
    const inRange = (d, s, e) => d >= s && d <= e;
    const aggRange = (start, end) => {
      const map = {};
      (logs || []).forEach(L => { const dt = new Date(L.finishedAt || L.startedAt); if (!inRange(dt, start, end)) return; const key = String(L.staffId || "unassigned"); map[key] = (map[key] || 0) + (Number(L.minutes) || 0); });
      return map;
    };
    return { currentWeek: aggRange(ws, we), lastWeek: aggRange(lws, lwe) };
  }, [logs, weekStartsOn]);

  const rows = useMemo(() => {
    const ids = new Set([...Object.keys(currentWeek), ...Object.keys(lastWeek), ...Object.keys(targets)]);
    return Array.from(ids).map(id => {
      const name = staff.find(s => String(s.id) === String(id))?.name || (id === "unassigned" ? "Unassigned" : `Staff ${id}`);
      const cur = +(currentWeek[id] || 0).toFixed(1);
      const prev = +(lastWeek[id] || 0).toFixed(1);
      const delta = +(cur - prev).toFixed(1);
      const target = Number(targets[id] || 0);
      const pct = target > 0 ? Math.max(0, Math.min(1, cur / target)) : null;
      return { id, name, cur, prev, delta, target, pct };
    }).sort((a,b)=> (b.cur - a.cur));
  }, [currentWeek, lastWeek, targets, staff]);

  const today = new Date();
  const weekday = today.getDay();

  return (
    <div className="rounded-2xl border p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="font-semibold">This Week — Minutes by Staff</div>
        <a href="/settings/staff-targets" className="text-xs underline opacity-70">Edit targets</a>
      </div>
      <div className="divide-y">
        {rows.length === 0 && <div className="text-sm opacity-70 py-2">No time logged yet.</div>}
        {rows.map(r => {
          const atRisk = r.target > 0 && weekday >= 4 && (r.cur / r.target) < 0.5;
          return (
            <div key={r.id} className="py-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-gray-100 border flex items-center justify-center text-xs">{initials(r.name)}</div>
                  <div className="text-sm">{r.name}</div>
                  {atRisk && <span className="ml-2 text-xs text-red-600 border border-red-200 rounded-full px-2">At risk</span>}
                </div>
                <div className="text-sm flex items-center gap-4">
                  <span className="font-semibold">{r.cur.toFixed(1)} min</span>
                  <DeltaBadge delta={r.delta} />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                  <div className={`h-full ${colorForPct(r.pct)} transition-all`} style={{ width: `${Math.round((r.pct || 0) * 100)}%` }} />
                </div>
                <div className="text-xs opacity-70 w-28 text-right">{r.target ? `${Math.round((r.pct || 0)*100)}% of ${r.target}m` : "No target"}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
function initials(name=""){const parts=String(name).trim().split(/\s+/);return (parts[0]?.[0]||"?")+(parts[1]?.[0]||"");}
function DeltaBadge({ delta }){const up=delta>0,down=delta<0;return(<span className={`text-xs px-2 py-0.5 rounded-full border ${up?"border-emerald-300 text-emerald-700 bg-emerald-50":down?"border-red-300 text-red-700 bg-red-50":"border-gray-300 text-gray-700 bg-gray-50"}`}>{delta>0?`▲ +${delta.toFixed(1)} min`:delta<0?`▼ ${delta.toFixed(1)} min`:"—"}</span>);}
function colorForPct(pct){if(pct==null)return"bg-gray-300";if(pct>=1)return"bg-emerald-500";if(pct>=0.75)return"bg-emerald-400";if(pct>=0.5)return"bg-amber-400";return"bg-red-400";}
function startOfWeek(date,weekStartsOn=1){const d=new Date(date);const day=(d.getDay()+7-weekStartsOn)%7;d.setHours(0,0,0,0);d.setDate(d.getDate()-day);return d;}
function endOfWeek(date,weekStartsOn=1){const s=startOfWeek(date,weekStartsOn);const e=new Date(s);e.setDate(e.getDate()+6);e.setHours(23,59,59,999);return e;}
